import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ChevronLeft } from 'lucide-react'

import { useClient } from '@/entities/client/model/useClient'
import { HotelReservationViewer } from '@/features/reservation-viewer/ui/HotelReservationViewer'
import { recalculateClientReservationsPrice } from '@/shared/api/reservationsAPI'
import { Button } from '@/shared/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card.tsx'
import { Loader } from '@/shared/ui/loader'
import { Client } from '@monorepo/functions/src/types/client'
import { type HairSalonReservation, type HotelReservation } from '@monorepo/functions/src/types/reservations'

import { ClientNotFound } from '../../entities/client/ui/client-not-found'
import { ClientClassificationSelector } from './entities/client/ui/client-classification-selector'
import { ClientPets } from './entities/client/ui/client-pets'
import { ClientSummary } from './entities/client/ui/client-summary'
import { ClientReservationHistoryCard } from './features/client-reservation-history/ClientReservationHistoryCard'

export default function ClientPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { client, setClientSync: setClient, isLoading } = useClient(id ?? '')
    const [selectedReservation, setSelectedReservation] = useState<HotelReservation | HairSalonReservation | null>(null)

    if (isLoading) {
        return <Loader />
    }

    if (!client) {
        return (
            <ClientNotFound
                additionalActions={
                    <Button asChild>
                        <Link to='/panel-interno/clientes'>Ver todos los clientes</Link>
                    </Button>
                }
            />
        )
    }

    const handleClassificationChange = async (classification: Client['classification']) => {
        await setClient({ ...client, classification })
        recalculateClientReservationsPrice({ clientId: client.id })
    }

    return (
        <div className='container mx-auto p-6'>
            <div className='mb-6 flex items-center justify-between'>
                <div className='flex items-center gap-4'>
                    <Button variant='ghost' size='icon' onClick={() => navigate(-1)}>
                        <ChevronLeft className='h-4 w-4' />
                    </Button>
                    <h1 className='text-3xl font-bold'>
                        {client.name} {client.lastName}
                    </h1>
                    <div className='flex items-center gap-2'>
                        <ClientClassificationSelector
                            client={client}
                            onClassificationChange={handleClassificationChange}
                        />
                    </div>
                </div>
            </div>

            <div className='grid gap-6 lg:grid-cols-3'>
                <div className='space-y-6 lg:col-span-2'>
                    <ClientSummary clientId={client.id} />

                    <ClientPets clientId={client.id} />

                    <ClientReservationHistoryCard
                        clientId={client.id}
                        onReservationClick={reservation => setSelectedReservation(reservation)}
                    />
                </div>

                <div className='space-y-6 lg:col-span-1'>
                    <Card>
                        <CardHeader>
                            <CardTitle>Acciones Adicionales</CardTitle>
                        </CardHeader>
                        <CardContent className='space-y-2'>
                            <Button className='w-full' asChild>
                                <Link to={`/booking?userId=${client.id}`}>Nueva Reserva</Link>
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {selectedReservation && (
                <HotelReservationViewer
                    reservation={selectedReservation}
                    onClose={() => setSelectedReservation(null)}
                    isOpen={!!selectedReservation}
                />
            )}
        </div>
    )
}
